/**
 * English bundle — the only bundle that exists (see i18n/index.ts). Keys are
 * namespaced per screen; a `common.*` namespace holds strings repeated
 * across screens (Refresh/Retry/etc) so they're translated once.
 */
export default {
  // ── common ──────────────────────────────────────────────────────────────
  'common.refresh': 'Refresh',
  'common.retry': 'Retry',
  'common.search': 'Search',
  'common.status': 'Status',
  'common.created': 'Created',

  // ── apiKeys screen ──────────────────────────────────────────────────────
  'apiKeys.title': 'API Keys & Tokens',
  'apiKeys.subtitle': 'Keys external callers use to POST /notify. Managed by this service — not platform credentials.',
  'apiKeys.justIssuedTitle': 'New key for "{{name}}" — copy it now.',
  'apiKeys.justIssuedBody': 'This is the only time the full key is shown.',
  'apiKeys.issueSection': 'Issue a new key',
  'apiKeys.nameLabel': 'Name',
  'apiKeys.namePlaceholder': 'e.g. acme-prod',
  'apiKeys.productLabel': 'Product',
  'apiKeys.productPlaceholder': 'e.g. works',
  'apiKeys.issuing': 'Issuing…',
  'apiKeys.issueKey': 'Issue key',
  'apiKeys.loading': 'Loading keys…',
  'apiKeys.loadError': "Couldn't load API keys",
  'apiKeys.thName': 'Name',
  'apiKeys.thProduct': 'Product',
  'apiKeys.thPrefix': 'Prefix',
  'apiKeys.thLastUsed': 'Last used',
  'apiKeys.badgeAdmin': 'admin',
  'apiKeys.badgeRevoked': 'revoked',
  'apiKeys.badgeActive': 'active',
  'apiKeys.revoke': 'Revoke',
  'apiKeys.emptyTitle': 'No API keys yet',
  'apiKeys.emptyBody': 'Issue one above for an external caller.',
  'apiKeys.toastIssuedTitle': 'API key issued',
  'apiKeys.toastIssuedBody': 'Copy it now — it is shown only once.',
  'apiKeys.toastRevokedTitle': 'Key revoked',
  'apiKeys.errIssue': 'Could not issue key',
  'apiKeys.errRevoke': 'Could not revoke',

  // ── health screen ───────────────────────────────────────────────────────
  'health.title': 'Health Dashboard',
  'health.subtitle': 'Real-time channel circuit state, queue depth, and delivery trends',
  'health.channelStatus': 'Channel Status',
  'health.channel.email': 'Email',
  'health.channel.whatsapp': 'WhatsApp',
  'health.channel.sms': 'SMS',
  'health.channel.push': 'Push',
  'health.failuresLastWindow': '{{count}} failures · last window',
  'health.primary': 'Primary',
  'health.fallback': 'Fallback',
  'health.failing': 'failing',
  'health.active': 'active',
  'health.servingAsFallback': 'serving as fallback',
  'health.standby': 'standby',
  'health.changedAt': 'Changed {{time}}',
  'health.noRecentChange': 'No recent state change',
  'health.resetCircuit': 'Reset Circuit',
  'health.queueStatus': 'Queue Status',
  'health.activeJobs': 'Active Jobs',
  'health.currentlyProcessing': 'currently processing',
  'health.delayedJobs': 'Delayed Jobs',
  'health.againstDelayedCap': 'against fallback delayed cap',
  'health.failedJobs': 'Failed Jobs',
  'health.last24h': 'last 24 hours',
  'health.viewAllInLog': 'View all in log',
  'health.deliveryMetrics': 'Delivery Metrics — {{window}}',
  'health.window12h': 'Last 12 hours',
  'health.window24h': 'Last 24 hours',
  'health.window7d': 'Last 7 days',
  'health.legendDelivered': 'Delivered',
  'health.legendPartial': 'Partial',
  'health.legendFailed': 'Failed',
  'health.noDeliveryData': 'No delivery data yet',
  'health.noDeliveryDataBody': 'Metrics will appear here once notifications are sent in this window.',
  'health.byChannel': 'By Channel',
  'health.bySource': 'By Source Module',
  'health.noDataYet': 'No data yet',
  'health.toastCircuitReset': 'Circuit reset — {{channel}}',
  'health.toastCircuitResetBody': 'State: CLOSED · monitoring resumed',
  'health.errResetFailed': 'Reset failed',

  // ── quotas screen ───────────────────────────────────────────────────────
  'quotas.title': 'Quota Management',
  'quotas.subtitle': 'Per-product, per-channel notification limits — read from platform licensing',
  'quotas.channel.email': 'Email',
  'quotas.channel.whatsapp': 'WhatsApp',
  'quotas.channel.sms': 'SMS',
  'quotas.channel.push': 'Push',
  'quotas.notLicensed': 'not licensed',
  'quotas.products': 'Products',
  'quotas.acrossPlatform': 'across platform',
  'quotas.healthy': 'Healthy',
  'quotas.under80': 'under 80% used',
  'quotas.nearLimit': 'Near limit',
  'quotas.between80and99': '80–99% used',
  'quotas.quotaExceeded': 'Quota exceeded',
  'quotas.blockingNewSends': 'blocking new sends',
  'quotas.searchPlaceholder': 'Search by product ID or name…',
  'quotas.filterAll': 'All products',
  'quotas.filterWarn': 'Over 80% used',
  'quotas.filterExceeded': 'Quota exceeded',
  'quotas.countOfTotal': '{{count}} of {{total}}',
  'quotas.loading': 'Loading usage…',
  'quotas.loadingBody': 'Fetching per-product quotas from the platform.',
  'quotas.loadError': "Couldn't load usage",
  'quotas.loadErrorBody': 'The platform product list or usage data is unavailable.',
  'quotas.active': '● Active',
  'quotas.quotaExceededPill': 'Quota Exceeded',
  'quotas.emptyNoProducts': 'No products found',
  'quotas.emptyNoMatch': 'No products match',
  'quotas.emptyNoProductsBody': 'The platform returned no products, or usage data is unavailable.',
  'quotas.emptyNoMatchBody': 'Adjust the search or filter to see more products.',
  'quotas.errLoad': 'Could not load usage',

  // ── log screen ──────────────────────────────────────────────────────────
  'log.status.queued': 'Queued',
  'log.status.processing': 'Processing',
  'log.status.delivered': 'Delivered',
  'log.status.partial': 'Partial',
  'log.status.failed': 'Failed',
  'log.status.validation_failed': 'Validation failed',
  'log.status.fallback_skipped': 'Fallback skipped',
  'log.status.quota_exceeded': 'Quota exceeded',
  'log.title': 'Notification Log',
  'log.subtitle': '{{shown}} of {{total}} results',
  'log.subtitlePage': ' · page {{page}} of {{pages}}',
  'log.pillTotal': 'Total',
  'log.pillDelivered': 'Delivered',
  'log.pillFailed': 'Failed',
  'log.pillPartial': 'Partial',
  'log.searchPlaceholder': 'Search jobId, template, referenceId…',
  'log.facetSource': 'Source',
  'log.facetStatus': 'Status',
  'log.facetChannel': 'Channel',
  'log.reset': 'Reset',
  'log.exportCsv': 'Export CSV',
  'log.loadErrorBanner': 'Failed to load logs. Check your API key and backend connection.',
  'log.loading': 'Loading…',
  'log.emptyTitle': 'No notifications found',
  'log.emptyBody': 'Try adjusting your filters or date range.',
  'log.thCreated': 'Created',
  'log.thSource': 'Source',
  'log.thTemplate': 'Template',
  'log.thJobId': 'Job ID',
  'log.thChannels': 'Channels',
  'log.thRetry': 'Retry',
  'log.thActions': 'Actions',
  'log.view': 'View',
  'log.retry': 'Retry',
  'log.pagerRange': '{{from}}–{{to}} of {{total}}',
  'log.prev': 'Prev',
  'log.next': 'Next',
  'log.toastRetryQueued': 'Queued for retry',
  'log.errRetryFailed': 'Retry failed',
  'log.toastExportReady': 'Export ready',
  'log.toastExportBody': '{{count}} rows exported.',
  'log.errLoadFailed': 'Failed to load logs',

  // ── sendTest screen ─────────────────────────────────────────────────────
  'sendTest.title': 'Send Test Notification',
  'sendTest.subtitle': 'Bypass normal triggers — manually queue a test message without code',
  'sendTest.liveWarningTitle': 'Live environment. ',
  'sendTest.liveWarningBody': 'Test notifications will be sent to real recipients. Verify the email and phone before submitting.',
  'sendTest.queuedTitle': 'Queued — 202 Accepted',
  'sendTest.queuedBody': 'Notification accepted by the queue and is now being processed.',
  'sendTest.duplicateTitle': 'Duplicate — 200 OK',
  'sendTest.duplicateBody': 'A notification with this jobId was already sent. Idempotency was honoured — no new send.',
  'sendTest.errorTitle': 'Error',
  'sendTest.sourceModule': 'Source Module',
  'sendTest.sourceModulePlaceholder': 'e.g. works, crm, skcal',
  'sendTest.template': 'Template',
  'sendTest.product': 'Product',
  'sendTest.productHint': 'The licensed product this send bills against (quota + channel licensing).',
  'sendTest.referenceId': 'Reference ID',
  'sendTest.referenceIdHint': 'Used to build jobId: {{source}}:{{ref}}',
  'sendTest.referenceIdPlaceholder': 'Auto-generated if empty',
  'sendTest.recipientName': 'Recipient Name',
  'sendTest.recipientNamePlaceholder': 'Deepak Kumar',
  'sendTest.email': 'Email',
  'sendTest.emailPlaceholder': 'recipient@example.com',
  'sendTest.phone': 'Phone',
  'sendTest.phoneHint': 'E.164 format, required for WhatsApp / SMS',
  'sendTest.phonePlaceholder': '+91XXXXXXXXXX',
  'sendTest.locale': 'Locale',
  'sendTest.channels': 'Channels',
  'sendTest.channelsHint': 'Select at least one channel.',
  'sendTest.notConfigured': 'not configured',
  'sendTest.templateData': 'Template Data (JSON)',
  'sendTest.jsonInvalid': 'JSON parse error — fix syntax before submitting.',
  'sendTest.jsonContentType': 'application/json',
  'sendTest.jsonValid': '● valid',
  'sendTest.jsonInvalidBadge': '● invalid syntax',
  'sendTest.fallbackRules': 'Fallback Rules',
  'sendTest.fallbackRulesHint': 'Optional. e.g. WhatsApp fails → Email after 5 mins.',
  'sendTest.addRule': 'Add rule',
  'sendTest.failsSendVia': 'fails → send via',
  'sendTest.after': 'after',
  'sendTest.min': 'min',
  'sendTest.toSendAdd': 'To send, add: {{blockers}}.',
  'sendTest.blockerName': 'recipient name',
  'sendTest.blockerEmail': 'email',
  'sendTest.blockerChannel': 'at least one channel',
  'sendTest.blockerJson': 'valid template JSON',
  'sendTest.blockerPhone': 'a valid E.164 phone (required for WhatsApp/SMS)',
  'sendTest.send': 'Send Notification',
  'sendTest.sending': 'Sending…',
  'sendTest.toastQueued': 'Notification queued',
} as Record<string, string>;
